<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>CPA 5G SA一覧</span>
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="cpa5gSa.search" v-model="cpa5gSa.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn
              v-permission.config.delegate
              color="info"
              @click="newContract()"
              >新規契約</v-btn
            >
            <v-btn
              v-permission.admin.delegate
              color="info"
              @click="startUsing()"
              >利用開始</v-btn
            >
            <v-btn :href="downloadUrl" color="primary">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="cpa5gSa.sortBy"
          primary-key="cpaContractSeq"
          :fields="cpa5gSa.fields"
          :items="cpa5gSa.items"
          :selectable="selectable"
          v-model="cpa5gSa.selected"
          :hover="true"
          @click:row="nwDetails"
          :filter="cpa5gSa.filter"
          :filter-option="cpa5gSa.filter_option"
          sticky-header="50vh"
          :td-class="cellClass"
        >
          <!-- 1件選択されたらチェックボックスを非活性にする -->
          <template #cell(_selected)="{ item }">
            <v-checkbox-btn
              :value="item"
              v-model="cpa5gSa.selected"
              @change="selectChange(item)"
              @click.stop
            />
          </template>
          <!-- エンドユーザ名 -->
          <template #cell(endUserName)="data">
            {{ $filter.emptyTo(data.item.endUserName, "-") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { CpaContract } from "@/apis/Cpa5gSaApi";
import SaNwReference from "@/modals/cpa5g/SaNwReference.vue";
import SaContractRegister from "@/modals/cpa5g/SaContractRegister.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import SaNwServiceStartUsingConfirm from "@/modals/cpa5g/SaNwServiceStartUsingConfirm.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "Cpa5gSaContractList",
  data() {
    return {
      isLoaded: false,
      alertMsg: null as string | null,
      downloadUrl: this.$api.cpa5gSa.downloadCpaContractCsvURL(),
      cpa5gSa: {
        sortBy: {
          key: "wnumber",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "wnumber",
            title: "CPA管理番号(Act/Sby)",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: CpaContract) => {
              if (item.wnumber) {
                return `${item.wnumber.wnumberAct}\n${item.wnumber.wnumberSby}`;
              } else {
                return "要NW設定";
              }
            },
          },
          {
            key: "domain",
            title: "ドメイン",
            width: "170px",
            sortable: true,
          },
          {
            key: "contractType",
            title: "契約タイプ",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string) => {
              return this.$filter.enumTo(value, "cpaContractType");
            },
          },
          {
            key: "vpnVnCode",
            title: "VPN/VNコード",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: CpaContract) => {
              return item.vpnVnCode.startsWith("B")
                ? item.vpnVnCode
                : item.vpnVnCode + "(" + item.vnName + ")";
            },
          },
          {
            key: "authenticationType",
            title: "認証タイプ",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string) => {
              return this.$filter.enumTo(value, "cpaAuthenticationType");
            },
          },
          {
            key: "bandwidthType",
            title: "品目",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string, key: null, item: CpaContract) => {
              let bandwidth;
              if (item.bandwidthType === "BEST_EFFORT") {
                bandwidth = "ベストエフォート:1Gbps";
              } else {
                if (!item.wnumber) {
                  bandwidth = "帯域確保";
                } else {
                  bandwidth = `帯域確保:${item.bandwidth!.value}`;
                  if (item.bandwidth?.unit) {
                    bandwidth += this.$filter.enumTo(
                      item.bandwidth.unit,
                      "bandwidth_unit"
                    );
                  }
                }
              }
              return bandwidth;
            },
          },
          {
            key: "addressAssignmentMethod",
            title: "アドレスアサイン方式",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string) => {
              return this.$filter.enumTo(value, "cpaAddressAssignMethod");
            },
          },
          {
            key: "endUserName",
            title: "エンドユーザ名",
            width: "170px",
            sortable: true,
          },
          {
            key: "status",
            title: "ステータス",
            width: "110px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string) => {
              return this.$filter.enumTo(value, "cpaContractStatus");
            },
          },
        ] as AppTableHeader<CpaContract>[],
        items: [] as CpaContract[],
        selected: [] as CpaContract[],
        search: {
          wnumber: { type: "text", label: "CPA管理番号(Act/Sby)" },
          domain: { type: "text", label: "ドメイン" },
          contractType: {
            type: "select",
            label: "契約タイプ",
            items: optionList.contractOptions,
          },
          vpnVnCode: {
            type: "text",
            label: "VPN/VNコード",
          },
          authenticationType: {
            type: "select",
            label: "認証タイプ",
            items: optionList.authOptions,
          },
          bandwidthType: {
            type: "select",
            label: "品目",
            items: optionList.itemOptions,
          },
          addressAssignmentMethod: {
            type: "select",
            label: "アドレスアサイン方式",
            items: optionList.infoAddressAssignOptions,
          },
          endUserName: { type: "text", label: "エンドユーザ名" },
          status: {
            type: "select",
            label: "ステータス",
            items: optionList.contractStatusOptions,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          wnumber: "like",
          domain: "like",
          contractType: "equal",
          vpnVnCode: "like",
          authenticationType: "equal",
          bandwidthType: "equal",
          addressAssignmentMethod: "equal",
          endUserName: "like",
          status: "equal",
        } as FilterOption,
      },
    };
  },
  /** true: 一覧選択可能, false: 不可 */
  computed: {
    selectable(): boolean {
      return (
        this.$service.permission.hasAuthority("ADMIN") &&
        this.$service.permission.isDelegateUser()
      );
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.cpa5gSa.items = (
        await this.$api.cpa5gSa.getCpaContracts()
      ).cpaContractList;
    },

    // 一件のみチェック可能なチェックボックスの実装
    selectChange(item: CpaContract) {
      // チェック済の場合にはチェックを外す為に配列に含まれるかを確認
      if (!this.cpa5gSa.selected.includes(item)) {
        this.cpa5gSa.selected.splice(0, this.cpa5gSa.selected.length);
      } else {
        this.cpa5gSa.selected.splice(0, this.cpa5gSa.selected.length);
        this.cpa5gSa.selected.push(item);
      }
    },

    /** 利用開始 */
    async startUsing() {
      // チェックが0件の場合アラート表示
      if (this.cpa5gSa.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        this.alertMsg = null;
        const selected = this.cpa5gSa.selected[0];
        if (selected.status !== "INACTIVE") {
          this.alertMsg = "利用開始前の回線を選択してください。";
        } else {
          await this.$modal.show(SaNwServiceStartUsingConfirm, {
            cpaContractInfo: selected,
          });
          await this.$api.cpa5gSa.startCpaContract(selected.cpaContractSeq, {
            cpaContractKeyId: selected.cpaContractKeyId,
          });
          await this.load();
        }
      }
    },

    /** 新規契約 */
    async newContract() {
      this.alertMsg = null;
      await this.$modal.show(SaContractRegister);
      await this.load();
    },

    /** NW設定情報参照 */
    async nwDetails(entity: CpaContract) {
      this.alertMsg = null;
      await this.$modal.show(SaNwReference, {
        cpaContractInfo: entity,
      });
      await this.load();
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<CpaContract>): string[] | undefined {
      if (field.key === "wnumber") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});

/** オプションをここにまとめる */
export const optionList = {
  contractOptions: [
    { value: "CPA_NO_LIMIT", text: "端末速度制限無し" },
    { value: "CPA_LIMIT_MODULE", text: "端末速度制限有り(モジュール専用)" },
    {
      value: "CPA_LIMIT_AUDIO",
      text: "端末速度制限有り(モジュール専用/音声有り)",
    },
  ],
  authOptions: [
    { value: "RADIUS", text: "RADIUS認証代行(端末認証あり)" },
    { value: "RADIUS_IMEI", text: "RADIUS認証代行(端末認証あり+IMEI認証あり)" },
    { value: "HOMERADIUS", text: "HomeRADIUS認証" },
    { value: "HOME", text: "端末認証(Home)" },
  ],
  redundancyOptions: [
    { value: "EAST_ACT_WEST_SBY", text: "東日本ACT/西日本SBY" },
    { value: "WEST_ACT_EAST_SBY", text: "西日本ACT/東日本SBY" },
  ],
  itemOptions: [
    { value: "BEST_EFFORT", text: "ベストエフォート" },
    { value: "SECURING", text: "帯域確保" },
  ],
  infoAddressAssignOptions: [
    { value: "FIXED_IP", text: "固定IP割り当て(ユーザアサイン)" },
    { value: "DYNAMIC_IP", text: "動的IP割り当て(キャリアアサイン)" },
  ],
  ipAddressOptions: [
    { value: "IPV4", text: "IPv4" },
    { value: "IPV6", text: "IPv6" },
    { value: "IPV4_IPV6", text: "IPv4/IPv6" },
  ],
  contractStatusOptions: [
    { value: "ACTIVE", text: "運用中" },
    { value: "ACTIVE_UPDATE", text: "運用中(未反映データあり)" },
    { value: "NW_SETTING_PREPARATION", text: "NW設定準備中" },
    { value: "INACTIVE", text: "利用開始前" },
  ],
  bpsOptions: [
    { value: "0.5", text: "0.5Mbps" },
    { value: "1", text: "1Mbps" },
    { value: "2", text: "2Mbps" },
    { value: "3", text: "3Mbps" },
    { value: "4", text: "4Mbps" },
    { value: "5", text: "5Mbps" },
    { value: "6", text: "6Mbps" },
    { value: "7", text: "7Mbps" },
    { value: "8", text: "8Mbps" },
    { value: "9", text: "9Mbps" },
    { value: "10", text: "10Mbps" },
    { value: "20", text: "20Mbps" },
    { value: "30", text: "30Mbps" },
    { value: "40", text: "40Mbps" },
    { value: "50", text: "50Mbps" },
    { value: "60", text: "60Mbps" },
    { value: "70", text: "70Mbps" },
    { value: "80", text: "80Mbps" },
    { value: "90", text: "90Mbps" },
    { value: "100", text: "100Mbps" },
    { value: "200", text: "200Mbps" },
    { value: "300", text: "300Mbps" },
    { value: "400", text: "400Mbps" },
    { value: "500", text: "500Mbps" },
    { value: "600", text: "600Mbps" },
    { value: "700", text: "700Mbps" },
    { value: "800", text: "800Mbps" },
    { value: "900", text: "900Mbps" },
    { value: "1000", text: "1Gbps" },
  ],
  udpOptions: [
    { value: "PORT_1645_1646", text: "1645/1646" },
    { value: "PORT_1812_1813", text: "1812/1813" },
  ],
  idleTimerList: [
    { value: "960", text: "960秒" },
    { value: "3900", text: "3900秒" },
    { value: "19800", text: "19800秒" },
    { value: "86400", text: "86400秒" },
  ],
};
</script>
